import { create } from 'zustand'
import { withDemo } from '@/config/demoMode'

export interface Notification {
  id: string
  type: 'deal' | 'trigger' | 'committee' | 'system' | 'report' | 'crm' | 'market' | 'ai'
  title: string
  message: string
  timestamp: string // ISO string
  read: boolean
  priority: 'high' | 'medium' | 'low'
  actionUrl?: string
  dealId?: string
  module?: string
  icon?: string // lucide icon name
  /** Delivered by a live source rather than the demo inbox. */
  live?: boolean
}

type NotificationFilterType = Notification['type'] | null

interface NotificationState {
  notifications: Notification[]
  showPanel: boolean
  filterType: NotificationFilterType
  unreadCount: number
  togglePanel: () => void
  markAsRead: (id: string) => void
  markAllRead: () => void
  dismissNotification: (id: string) => void
  setFilterType: (type: NotificationFilterType) => void
  /** Ids the reader dismissed; a live source never brings one back. */
  dismissedIds: string[]
  /** Replace the live notifications of one type with the source's current
   *  set, keeping each one's read state. The first live source is the open
   *  triggers on the active book (`LiveNotificationSync`); before it the bell
   *  had no source at all and could never show anything (EXEC-12). */
  syncLive: (type: Notification['type'], items: Notification[]) => void
}

function computeUnread(notifications: Notification[]): number {
  return notifications.filter((n) => !n.read).length
}

// Helper: produce ISO timestamps relative to "now"
function ago(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString()
}

const MOCK_NOTIFICATIONS: Notification[] = [
  // ── TODAY ──────────────────────────────────────────────────────────────
  {
    id: 'ntf-001',
    type: 'deal',
    title: 'Deal ALG-1007 stage change',
    message: 'Deal ALG-1007 moved to Commitment Issued — Parkway Industrial Portfolio.',
    timestamp: ago(2),
    read: false,
    priority: 'medium',
    actionUrl: '/pipeline',
    dealId: 'ALG-1007',
    module: 'pipeline',
    icon: 'GitBranch',
  },
  {
    id: 'ntf-002',
    type: 'trigger',
    title: 'DSCR covenant breach',
    message: 'Riverwalk at Tempe Town Lake — DSCR dropped below 1.20x threshold (current 1.14x).',
    timestamp: ago(15),
    read: false,
    priority: 'high',
    actionUrl: '/surveillance',
    dealId: 'ALG-1012',
    module: 'surveillance',
    icon: 'AlertTriangle',
  },
  {
    id: 'ntf-003',
    type: 'committee',
    title: 'Committee memo approved',
    message: 'The Ellington at Uptown — Investment committee memo approved unanimously.',
    timestamp: ago(60),
    read: false,
    priority: 'medium',
    actionUrl: '/committee',
    dealId: 'ALG-1005',
    module: 'committee',
    icon: 'Scale',
  },
  {
    id: 'ntf-004',
    type: 'crm',
    title: 'New broker request received',
    message: 'CBRE — $42M Multifamily acquisition in Phoenix, AZ. Contact: David Liu.',
    timestamp: ago(120),
    read: false,
    priority: 'medium',
    actionUrl: '/intake',
    module: 'intake',
    icon: 'UserPlus',
  },
  {
    id: 'ntf-005',
    type: 'report',
    title: 'Monthly surveillance report generated',
    message: 'March 2026 surveillance report is ready for review — 18 loans, 3 watchlist items.',
    timestamp: ago(180),
    read: false,
    priority: 'low',
    actionUrl: '/reports',
    module: 'reports',
    icon: 'FileText',
  },
  {
    id: 'ntf-006',
    type: 'market',
    title: 'SOFR rate updated',
    message: 'SOFR rate updated to 4.33% (+2bp). Floating-rate portfolio impact: +$142K annual interest.',
    timestamp: ago(240),
    read: false,
    priority: 'low',
    module: 'market-intelligence',
    icon: 'TrendingUp',
  },
  {
    id: 'ntf-007',
    type: 'ai',
    title: 'AI risk analysis complete',
    message: 'AI Agent completed risk analysis on Gateway Logistics Center — 3 findings flagged.',
    timestamp: ago(300),
    read: false,
    priority: 'medium',
    actionUrl: '/underwriting',
    dealId: 'ALG-1009',
    module: 'underwriting',
    icon: 'Brain',
  },
  {
    id: 'ntf-008',
    type: 'system',
    title: 'Login from new IP address',
    message: 'User login detected from 72.14.205.100 (Mountain View, CA). Not your usual location.',
    timestamp: ago(360),
    read: false,
    priority: 'high',
    module: 'system',
    icon: 'Shield',
  },
  {
    id: 'ntf-009',
    type: 'trigger',
    title: 'Appraisal overdue',
    message: 'One Liberty Plaza — appraisal 14 days past due. Escalation required.',
    timestamp: ago(480),
    read: false,
    priority: 'high',
    actionUrl: '/surveillance',
    dealId: 'ALG-1015',
    module: 'surveillance',
    icon: 'Clock',
  },
  {
    id: 'ntf-010',
    type: 'deal',
    title: 'Rate lock expiring soon',
    message: 'Parkway Industrial Portfolio — rate lock expires in 48 hours. Current lock: 5.65%.',
    timestamp: ago(600),
    read: false,
    priority: 'high',
    actionUrl: '/closing',
    dealId: 'ALG-1007',
    module: 'closing',
    icon: 'Lock',
  },
  {
    id: 'ntf-011',
    type: 'ai',
    title: 'Comp analysis updated',
    message: 'AI-powered comp set refreshed for Harbor Point Residences — 12 new comps within 1mi.',
    timestamp: ago(660),
    read: true,
    priority: 'low',
    actionUrl: '/valuation',
    dealId: 'ALG-1001',
    module: 'valuation',
    icon: 'Brain',
  },
  {
    id: 'ntf-012',
    type: 'deal',
    title: 'Document uploaded',
    message: 'Phase I ESA uploaded for Pinnacle Tower by Marcus Webb.',
    timestamp: ago(720),
    read: true,
    priority: 'low',
    actionUrl: '/underwriting',
    dealId: 'ALG-1002',
    module: 'underwriting',
    icon: 'FileText',
  },

  // ── YESTERDAY ─────────────────────────────────────────────────────────
  {
    id: 'ntf-013',
    type: 'committee',
    title: 'Committee meeting scheduled',
    message: 'Investment committee meeting set for Thursday at 2:00 PM — 3 deals on agenda.',
    timestamp: ago(1500),
    read: true,
    priority: 'medium',
    actionUrl: '/committee',
    module: 'committee',
    icon: 'Calendar',
  },
  {
    id: 'ntf-014',
    type: 'crm',
    title: 'Borrower follow-up due',
    message: 'Follow-up overdue: Trident Residential Partners — Harbor Point Residences.',
    timestamp: ago(1600),
    read: false,
    priority: 'medium',
    actionUrl: '/pipeline',
    dealId: 'ALG-1001',
    module: 'pipeline',
    icon: 'Phone',
  },
  {
    id: 'ntf-015',
    type: 'trigger',
    title: 'Insurance policy expiring',
    message: 'Meridian Business Park — property insurance expires in 30 days. Renewal needed.',
    timestamp: ago(1700),
    read: true,
    priority: 'medium',
    actionUrl: '/surveillance',
    dealId: 'ALG-1010',
    module: 'surveillance',
    icon: 'AlertTriangle',
  },
  {
    id: 'ntf-016',
    type: 'market',
    title: '10Y Treasury yield spike',
    message: '10Y Treasury yield jumped 8bp to 4.52% — monitor spread-sensitive deals.',
    timestamp: ago(1800),
    read: true,
    priority: 'medium',
    module: 'market-intelligence',
    icon: 'TrendingUp',
  },
  {
    id: 'ntf-017',
    type: 'deal',
    title: 'Term sheet sent',
    message: 'Term sheet delivered to Aspen Ridge Capital for Cascade Falls Village — $38M bridge.',
    timestamp: ago(1900),
    read: true,
    priority: 'medium',
    actionUrl: '/screening',
    dealId: 'ALG-1004',
    module: 'screening',
    icon: 'Send',
  },
  {
    id: 'ntf-018',
    type: 'system',
    title: 'Scheduled maintenance',
    message: 'Planned system maintenance Sunday 2:00-4:00 AM ET. Brief downtime expected.',
    timestamp: ago(2000),
    read: true,
    priority: 'low',
    module: 'system',
    icon: 'Settings',
  },
  {
    id: 'ntf-019',
    type: 'report',
    title: 'Quarterly pipeline report',
    message: 'Q1 2026 pipeline report generated — $847M across 18 active deals.',
    timestamp: ago(2100),
    read: true,
    priority: 'low',
    actionUrl: '/reports',
    module: 'reports',
    icon: 'BarChart3',
  },
  {
    id: 'ntf-020',
    type: 'ai',
    title: 'Anomaly detected in financials',
    message: 'AI flagged unusual expense increase (+34% YoY) on Sage Creek Office Campus T-12.',
    timestamp: ago(2200),
    read: false,
    priority: 'high',
    actionUrl: '/underwriting',
    dealId: 'ALG-1006',
    module: 'underwriting',
    icon: 'Brain',
  },

  // ── EARLIER THIS WEEK ─────────────────────────────────────────────────
  {
    id: 'ntf-021',
    type: 'deal',
    title: 'New deal received',
    message: 'Silverline Ventures submitted $28M retail acquisition — Downtown Austin.',
    timestamp: ago(4300),
    read: true,
    priority: 'medium',
    actionUrl: '/intake',
    dealId: 'ALG-1018',
    module: 'intake',
    icon: 'Inbox',
  },
  {
    id: 'ntf-022',
    type: 'trigger',
    title: 'Occupancy drop alert',
    message: 'Pinnacle Tower occupancy fell to 78% — below 85% covenant threshold.',
    timestamp: ago(5000),
    read: true,
    priority: 'high',
    actionUrl: '/surveillance',
    dealId: 'ALG-1002',
    module: 'surveillance',
    icon: 'AlertTriangle',
  },
  {
    id: 'ntf-023',
    type: 'crm',
    title: 'New relationship added',
    message: 'JLL Capital Markets — new broker relationship registered by Sarah Chen.',
    timestamp: ago(5800),
    read: true,
    priority: 'low',
    module: 'pipeline',
    icon: 'Users',
  },
  {
    id: 'ntf-024',
    type: 'market',
    title: 'CRE market brief published',
    message: 'Weekly CRE market brief: Multifamily cap rates compress 15bp in Sun Belt metros.',
    timestamp: ago(6500),
    read: true,
    priority: 'low',
    module: 'market-intelligence',
    icon: 'Globe',
  },
  {
    id: 'ntf-025',
    type: 'committee',
    title: 'Vote recorded',
    message: 'Committee vote recorded: Gateway Logistics Center approved 4-1 with conditions.',
    timestamp: ago(7200),
    read: true,
    priority: 'medium',
    actionUrl: '/committee',
    dealId: 'ALG-1009',
    module: 'committee',
    icon: 'CheckCircle',
  },
  {
    id: 'ntf-026',
    type: 'system',
    title: 'Data export complete',
    message: 'Portfolio data export (CSV) completed — 847 records, 2.3 MB. Ready for download.',
    timestamp: ago(8000),
    read: true,
    priority: 'low',
    module: 'system',
    icon: 'Download',
  },
  {
    id: 'ntf-027',
    type: 'report',
    title: 'Delinquency report ready',
    message: 'Monthly delinquency report: 0 loans 30+ days past due. Portfolio performing.',
    timestamp: ago(8500),
    read: true,
    priority: 'low',
    actionUrl: '/reports',
    module: 'reports',
    icon: 'FileText',
  },
  {
    id: 'ntf-028',
    type: 'deal',
    title: 'Closing checklist complete',
    message: 'All closing conditions satisfied for The Ellington at Uptown — ready to fund.',
    timestamp: ago(9000),
    read: true,
    priority: 'medium',
    actionUrl: '/closing',
    dealId: 'ALG-1005',
    module: 'closing',
    icon: 'CheckCircle',
  },
]

// Demo notifications only when demo mode is on; otherwise start with an empty inbox.
const INITIAL_NOTIFICATIONS = withDemo(MOCK_NOTIFICATIONS, [] as Notification[])

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: INITIAL_NOTIFICATIONS,
  showPanel: false,
  filterType: null,
  unreadCount: computeUnread(INITIAL_NOTIFICATIONS),

  togglePanel: () => set((s) => ({ showPanel: !s.showPanel })),

  markAsRead: (id: string) => {
    const updated = get().notifications.map((n) =>
      n.id === id ? { ...n, read: true } : n
    )
    set({ notifications: updated, unreadCount: computeUnread(updated) })
  },

  markAllRead: () => {
    const updated = get().notifications.map((n) => ({ ...n, read: true }))
    set({ notifications: updated, unreadCount: 0 })
  },

  dismissNotification: (id: string) => {
    const updated = get().notifications.filter((n) => n.id !== id)
    set({
      notifications: updated,
      unreadCount: computeUnread(updated),
      dismissedIds: [...get().dismissedIds, id],
    })
  },

  setFilterType: (type: NotificationFilterType) => set({ filterType: type }),

  dismissedIds: [],

  syncLive: (type, items) => {
    const { notifications, dismissedIds } = get()
    const current = notifications.filter((n) => n.live && n.type === type)
    const previous = new Map(current.map((n) => [n.id, n]))
    const dismissed = new Set(dismissedIds)
    const incoming = items
      .filter((item) => !dismissed.has(item.id))
      .map((item) => ({ ...item, live: true, read: previous.get(item.id)?.read ?? item.read }))
    // No write when nothing changed: every subscriber re-renders on a set(),
    // and a source that re-sends the same set on each render would loop.
    const unchanged =
      incoming.length === current.length &&
      incoming.every((item, index) => {
        const before = current[index]
        return (
          before.id === item.id &&
          before.title === item.title &&
          before.message === item.message &&
          before.priority === item.priority &&
          before.timestamp === item.timestamp
        )
      })
    if (unchanged) return
    const kept = notifications.filter((n) => !(n.live && n.type === type))
    const updated = [...kept, ...incoming]
    set({ notifications: updated, unreadCount: computeUnread(updated) })
  },
}))
